import {useState, useRef, useEffect, useMemo} from 'react'
import {
  AlignLeft,
  Calendar,
  CheckCircle2,
  Clock3,
  Edit3,
  Flag,
  Gauge,
  LayoutPanelTop,
  Layers3,
  ListChecks,
  MessageSquare,
  Paperclip,
  RotateCcw,
  Save,
  Search,
  Tag,
  Trash2,
  User,
  X,
  Zap,
} from 'lucide-react'
import type {Task, Priority, SubTask} from '../../types'
import {useApp} from '../../context/AppContext'
import {getStatusConfig, getPriorityConfig, formatDate} from '../../utils/helpers'
import Avatar from '../common/Avatar'
import Badge from '../common/Badge'
import {LabelBadge} from '../common/Badge'
import ConfirmDialog from '../common/ConfirmDialog'
import RichTextEditor from '../common/RichTextEditor'
import DatePickerDropdown from '../common/DatePickerDropdown'
import TaskComments from './TaskComments'
import TaskAttachments from './TaskAttachments'
import SubtaskList from './SubtaskList'

interface TaskModalProps {
  task: Task
  onClose: () => void
}

const priorities: Priority[] = ['low', 'medium', 'high', 'critical']

export default function TaskModal({task, onClose}: TaskModalProps) {
  const {
    getUserById,
    getLabelById,
    state,
    apiUpdateTask,
    apiDeleteTask,
    apiAddSubtask,
    apiUpdateSubtask,
    apiDeleteSubtask,
    getBoardById,
  } = useApp()

  const board = getBoardById(task.boardId)
  const boardStatuses = board?.columns.map((col) => col.status) || []

  const [isEditing, setIsEditing] = useState(false)
  const [activeTab, setActiveTab] = useState<'comments' | 'attachments' | 'subtasks'>('comments')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [assigneeSearch, setAssigneeSearch] = useState('')
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const assigneeDropdownRef = useRef<HTMLDivElement>(null)
  const activeTaskIdRef = useRef(task.id)

  const [draftTask, setDraftTask] = useState<Task>(task)

  useEffect(() => {
    if (activeTaskIdRef.current === task.id) return

    activeTaskIdRef.current = task.id
    setDraftTask(task)
    setIsEditing(false)
    setSaveError('')
    setAssigneeSearch('')
    setShowAssigneeDropdown(false)
  }, [task])

  const reporter = getUserById(draftTask.reporterId)
  const assignees = draftTask.assigneeIds.map((id) => getUserById(id)).filter(Boolean)
  const taskLabels = draftTask.labelIds.map((id) => getLabelById(id)).filter(Boolean)
  const status = getStatusConfig(draftTask.status)
  const priority = getPriorityConfig(draftTask.priority)
  const sprint = state.sprints.find((item) => item.id === draftTask.sprintId)
  const boardSprints = state.sprints.filter((item) => item.boardId === draftTask.boardId)
  const completedSubtasks = draftTask.subtasks.filter((item) => item.completed).length
  const subtaskProgress =
    draftTask.subtasks.length > 0
      ? Math.round((completedSubtasks / draftTask.subtasks.length) * 100)
      : 0
  const availableStatuses = boardStatuses.includes(draftTask.status)
    ? boardStatuses
    : [draftTask.status, ...boardStatuses]
  const taskReference = `#${draftTask.id.slice(-8).toUpperCase()}`
  const dueDateLabel = draftTask.dueDate ? formatDate(draftTask.dueDate) : 'Sem prazo'

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(e.target as Node)) {
        setShowAssigneeDropdown(false)
        setAssigneeSearch('')
      }
    }

    if (showAssigneeDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showAssigneeDropdown])

  const filteredUsers = state.users.filter(
    (u) =>
      !draftTask.assigneeIds.includes(u.id) &&
      u.name.toLowerCase().includes(assigneeSearch.toLowerCase())
  )

  const isDirty = useMemo(() => {
    return (
      JSON.stringify({
        title: draftTask.title,
        description: draftTask.description,
        status: draftTask.status,
        priority: draftTask.priority,
        assigneeIds: draftTask.assigneeIds,
        labelIds: draftTask.labelIds,
        dueDate: draftTask.dueDate,
        sprintId: draftTask.sprintId,
        storyPoints: draftTask.storyPoints,
        subtasks: draftTask.subtasks,
      }) !==
      JSON.stringify({
        title: task.title,
        description: task.description,
        status: task.status,
        priority: task.priority,
        assigneeIds: task.assigneeIds,
        labelIds: task.labelIds,
        dueDate: task.dueDate,
        sprintId: task.sprintId,
        storyPoints: task.storyPoints,
        subtasks: task.subtasks,
      })
    )
  }, [draftTask, task])

  const updateDraftTask = (partial: Partial<Task>) => {
    setDraftTask((prev) => ({...prev, ...partial}))
  }

  const handleStartEditing = () => {
    setIsEditing(true)
    setSaveError('')
  }

  async function handleSaveAll() {
    if (isSaving || !isDirty) return

    setIsSaving(true)
    setSaveError('')

    try {
      const taskPayload: Partial<Task> = {}

      if (draftTask.title !== task.title) taskPayload.title = draftTask.title
      if (draftTask.description !== task.description)
        taskPayload.description = draftTask.description
      if (draftTask.status !== task.status) taskPayload.status = draftTask.status
      if (draftTask.priority !== task.priority) taskPayload.priority = draftTask.priority
      if (JSON.stringify(draftTask.assigneeIds) !== JSON.stringify(task.assigneeIds)) {
        taskPayload.assigneeIds = draftTask.assigneeIds
      }
      if (JSON.stringify(draftTask.labelIds) !== JSON.stringify(task.labelIds)) {
        taskPayload.labelIds = draftTask.labelIds
      }
      if (draftTask.dueDate !== task.dueDate) taskPayload.dueDate = draftTask.dueDate
      if (draftTask.sprintId !== task.sprintId) taskPayload.sprintId = draftTask.sprintId
      if (draftTask.storyPoints !== task.storyPoints) {
        taskPayload.storyPoints = draftTask.storyPoints
      }

      if (Object.keys(taskPayload).length > 0) {
        await apiUpdateTask({id: task.id, ...taskPayload})
      }

      const originalSubtasks = task.subtasks
      const nextSubtasks = draftTask.subtasks

      const originalMap = new Map(originalSubtasks.map((s) => [s.id, s]))
      const nextMap = new Map(nextSubtasks.map((s) => [s.id, s]))

      const toCreate = nextSubtasks.filter((s) => s.id.startsWith('temp-'))
      const toDelete = originalSubtasks.filter((s) => !nextMap.has(s.id))
      const toUpdate = nextSubtasks.filter((s) => {
        if (s.id.startsWith('temp-')) return false
        const original = originalMap.get(s.id)
        if (!original) return false

        return (
          JSON.stringify({
            title: s.title,
            assigneeId: s.assigneeId,
            hours: s.hours,
            startDate: s.startDate,
            endDate: s.endDate,
            completed: s.completed,
          }) !==
          JSON.stringify({
            title: original.title,
            assigneeId: original.assigneeId,
            hours: original.hours,
            startDate: original.startDate,
            endDate: original.endDate,
            completed: original.completed,
          })
        )
      })

      await Promise.all([
        ...toCreate.map((subtask) =>
          apiAddSubtask(task.id, {
            title: subtask.title,
            assigneeId: subtask.assigneeId ?? null,
            hours: subtask.hours ?? 0,
            startDate: subtask.startDate ?? null,
            endDate: subtask.endDate ?? null,
            completed: !!subtask.completed,
          })
        ),
        ...toUpdate.map((subtask) =>
          apiUpdateSubtask({
            id: subtask.id,
            title: subtask.title,
            assigneeId: subtask.assigneeId ?? null,
            hours: subtask.hours ?? 0,
            startDate: subtask.startDate ?? null,
            endDate: subtask.endDate ?? null,
            completed: !!subtask.completed,
          })
        ),
        ...toDelete.map((subtask) => apiDeleteSubtask(subtask.id)),
      ])
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to update task:', error)
      setSaveError('Não foi possível salvar as alterações. Tente novamente.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelEditing = () => {
    setDraftTask(task)
    setIsEditing(false)
    setSaveError('')
    setAssigneeSearch('')
    setShowAssigneeDropdown(false)
  }

  const handleRequestClose = () => {
    if (isDirty) {
      setShowDiscardConfirm(true)
      return
    }
    onClose()
  }

  return (
    <div className='devops-task-modal-root fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 animate-fade-in'>
      <div className='devops-task-modal-backdrop' onClick={handleRequestClose} aria-hidden='true' />

      <div
        className='devops-task-workspace animate-scale-in'
        role='dialog'
        aria-modal='true'
        aria-labelledby='devops-task-modal-title'
      >
        <header className='devops-task-modal-header'>
          <div className='devops-task-modal-heading'>
            <span className='devops-task-modal-heading__icon'>
              <LayoutPanelTop size={20} strokeWidth={1.9} />
            </span>
            <div className='min-w-0'>
              <div className='devops-task-modal-kicker'>
                <span>{board?.name || 'Quadro de trabalho'}</span>
                <span>{taskReference}</span>
                <span>{isEditing ? 'Modo de edição' : 'Visualização da tarefa'}</span>
              </div>
              {isEditing ? (
                <input
                  id='devops-task-modal-title'
                  type='text'
                  value={draftTask.title}
                  onChange={(event) => updateDraftTask({title: event.target.value})}
                  className='w-full min-w-0 rounded-xl border border-brand-200 bg-white px-3 py-2 text-lg font-semibold text-[#264073] outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100'
                  placeholder='Título da tarefa'
                  autoFocus
                />
              ) : (
                <div className='flex min-w-0 items-center gap-2'>
                  <h2 id='devops-task-modal-title' className='devops-task-modal-title truncate'>
                    {draftTask.title}
                  </h2>
                  <Badge className={status.color}>{status.label}</Badge>
                </div>
              )}
            </div>
          </div>

          <div className='devops-task-modal-actions'>
            {isDirty && (
              <span className='hidden md:inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700'>
                Alterações pendentes
              </span>
            )}
            <button
              type='button'
              onClick={() => setShowDeleteConfirm(true)}
              className='devops-icon-button hover:!border-red-200 hover:!bg-red-50 hover:!text-red-500'
              aria-label='Excluir tarefa'
            >
              <Trash2 size={17} />
            </button>
            <button
              type='button'
              onClick={handleRequestClose}
              className='devops-icon-button'
              aria-label='Fechar modal'
            >
              <X size={19} />
            </button>
          </div>
        </header>

        <div className='devops-task-modal-body'>
          <main className='devops-task-main'>
            <div className='devops-task-summary' aria-label='Resumo da tarefa'>
              <SummaryCard icon={Flag} label='Status' value={status.label} />
              <SummaryCard
                icon={CheckCircle2}
                label='Subtarefas'
                value={
                  draftTask.subtasks.length > 0
                    ? `${completedSubtasks}/${draftTask.subtasks.length} · ${subtaskProgress}%`
                    : 'Nenhuma cadastrada'
                }
              />
              <SummaryCard
                icon={Gauge}
                label='Estimativa'
                value={`${draftTask.storyPoints || 0} pontos`}
              />
              <SummaryCard icon={Calendar} label='Prazo' value={dueDateLabel} />
            </div>

            <section className='devops-task-section'>
              <div className='devops-task-section__heading'>
                <h3>
                  <AlignLeft size={16} />
                  Descrição
                </h3>
                {!isEditing && (
                  <button
                    type='button'
                    onClick={handleStartEditing}
                    className='text-xs font-semibold text-brand-600 hover:text-brand-700'
                  >
                    Editar conteúdo
                  </button>
                )}
              </div>
              {isEditing ? (
                <div className='devops-task-description bg-white'>
                  <RichTextEditor
                    value={draftTask.description}
                    onChange={(value) => updateDraftTask({description: value})}
                    placeholder='Descreva o contexto, o objetivo e os critérios de aceite...'
                    minHeight='180px'
                  />
                </div>
              ) : (
                <div className='devops-task-description'>
                  {draftTask.description ? (
                    <div
                      dangerouslySetInnerHTML={{__html: draftTask.description}}
                      className='prose prose-sm max-w-none text-slate-600'
                    />
                  ) : (
                    <div className='grid min-h-[100px] place-items-center text-center'>
                      <div>
                        <AlignLeft size={21} className='mx-auto mb-2 text-slate-300' />
                        <p className='m-0 text-sm text-slate-400'>
                          Esta tarefa ainda não possui descrição.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className='devops-task-section mb-0'>
              <div className='devops-task-tabs' role='tablist' aria-label='Conteúdo da tarefa'>
                <TaskTab
                  active={activeTab === 'comments'}
                  icon={MessageSquare}
                  label='Comentários'
                  count={task.comments.length}
                  onClick={() => setActiveTab('comments')}
                />
                <TaskTab
                  active={activeTab === 'attachments'}
                  icon={Paperclip}
                  label='Anexos'
                  count={task.attachments.length}
                  onClick={() => setActiveTab('attachments')}
                />
                <TaskTab
                  active={activeTab === 'subtasks'}
                  icon={ListChecks}
                  label='Subtarefas'
                  count={draftTask.subtasks.length}
                  onClick={() => setActiveTab('subtasks')}
                />
              </div>

              <div className='devops-task-tab-panel' role='tabpanel'>
                {activeTab === 'comments' && (
                  <TaskComments taskId={task.id} comments={task.comments} />
                )}
                {activeTab === 'attachments' && (
                  <TaskAttachments taskId={task.id} attachments={task.attachments} />
                )}
                {activeTab === 'subtasks' &&
                  (isEditing ? (
                    <SubtaskList
                      subtasks={draftTask.subtasks}
                      onChange={(subtasks: SubTask[]) => updateDraftTask({subtasks})}
                    />
                  ) : (
                    <ReadOnlySubtasks subtasks={draftTask.subtasks} getUserById={getUserById} />
                  ))}
              </div>
            </section>
          </main>

          <aside className='devops-task-sidebar'>
            <section className='devops-task-sidebar-section'>
              <h3 className='devops-task-sidebar-title'>
                <Zap size={15} />
                Planejamento
              </h3>

              <SidebarField icon={Flag} label='Status'>
                {isEditing ? (
                  <select
                    value={draftTask.status}
                    onChange={(event) => updateDraftTask({status: event.target.value})}
                    className='w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm'
                    aria-label='Status da tarefa'
                  >
                    {availableStatuses.map((item) => (
                      <option key={item} value={item}>
                        {getStatusConfig(item).label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className='devops-task-field__value'>
                    <Badge className={status.color}>{status.label}</Badge>
                  </div>
                )}
              </SidebarField>

              <SidebarField icon={Zap} label='Prioridade'>
                {isEditing ? (
                  <select
                    value={draftTask.priority}
                    onChange={(event) =>
                      updateDraftTask({priority: event.target.value as Priority})
                    }
                    className='w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm'
                    aria-label='Prioridade da tarefa'
                  >
                    {priorities.map((item) => (
                      <option key={item} value={item}>
                        {getPriorityConfig(item).label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className='devops-task-field__value'>
                    <Badge className={priority.color}>{priority.label}</Badge>
                  </div>
                )}
              </SidebarField>

              <SidebarField icon={Layers3} label='Sprint'>
                {isEditing ? (
                  <select
                    value={draftTask.sprintId || ''}
                    onChange={(event) => updateDraftTask({sprintId: event.target.value || null})}
                    className='w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm'
                    aria-label='Sprint da tarefa'
                  >
                    <option value=''>Sem sprint</option>
                    {boardSprints.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className='devops-task-field__value'>{sprint?.name || 'Sem sprint'}</div>
                )}
              </SidebarField>

              <div className='grid grid-cols-2 gap-3'>
                <SidebarField icon={Gauge} label='Pontos'>
                  {isEditing ? (
                    <input
                      type='number'
                      min={0}
                      max={100}
                      value={draftTask.storyPoints}
                      onChange={(event) =>
                        updateDraftTask({storyPoints: Math.max(0, Number(event.target.value) || 0)})
                      }
                      className='w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm'
                      aria-label='Pontos da tarefa'
                    />
                  ) : (
                    <div className='devops-task-field__value'>{draftTask.storyPoints || 0}</div>
                  )}
                </SidebarField>
                <SidebarField icon={Calendar} label='Prazo'>
                  {isEditing ? (
                    <DatePickerDropdown
                      value={draftTask.dueDate ? draftTask.dueDate.split('T')[0] : null}
                      onChange={(value) =>
                        updateDraftTask({dueDate: value ? `${value}T23:59:59Z` : null})
                      }
                      placeholder='Selecionar'
                      showPresets
                    />
                  ) : (
                    <div className='devops-task-field__value'>{dueDateLabel}</div>
                  )}
                </SidebarField>
              </div>
            </section>

            <section className='devops-task-sidebar-section'>
              <h3 className='devops-task-sidebar-title'>
                <User size={15} />
                Pessoas e organização
              </h3>
              <SidebarField icon={User} label='Responsáveis'>
                <div className='space-y-2'>
                  {assignees.map((user) => (
                    <div
                      key={user!.id}
                      className='flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2'
                    >
                      <Avatar name={user!.name} color={user!.avatarColor} size='sm' />
                      <span className='min-w-0 flex-1 truncate text-sm text-slate-700'>
                        {user!.name}
                      </span>
                      {isEditing && (
                        <button
                          type='button'
                          onClick={() =>
                            updateDraftTask({
                              assigneeIds: draftTask.assigneeIds.filter((id) => id !== user!.id),
                            })
                          }
                          className='rounded p-0.5 text-slate-300 hover:bg-red-50 hover:text-red-500'
                          aria-label={`Remover ${user!.name}`}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                  {assignees.length === 0 && (
                    <div className='devops-task-field__value text-slate-400'>
                      Sem responsável definido
                    </div>
                  )}
                  {isEditing && (
                    <div ref={assigneeDropdownRef} className='relative'>
                      <div className='flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-100'>
                        <Search size={14} className='shrink-0 text-slate-400' />
                        <input
                          type='text'
                          value={assigneeSearch}
                          onChange={(event) => {
                            setAssigneeSearch(event.target.value)
                            setShowAssigneeDropdown(true)
                          }}
                          onFocus={() => setShowAssigneeDropdown(true)}
                          placeholder='Adicionar responsável...'
                          className='w-full bg-transparent text-sm outline-none'
                          aria-label='Adicionar responsável'
                        />
                      </div>
                      {showAssigneeDropdown && (
                        <div className='absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg'>
                          <div className='max-h-48 overflow-y-auto py-1'>
                            {filteredUsers.map((user) => (
                              <button
                                key={user.id}
                                type='button'
                                onClick={() => {
                                  updateDraftTask({
                                    assigneeIds: [...draftTask.assigneeIds, user.id],
                                  })
                                  setAssigneeSearch('')
                                  setShowAssigneeDropdown(false)
                                }}
                                className='flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-brand-50'
                              >
                                <Avatar name={user.name} color={user.avatarColor} size='sm' />
                                <span className='truncate'>{user.name}</span>
                              </button>
                            ))}
                            {filteredUsers.length === 0 && (
                              <div className='px-3 py-3 text-center text-xs text-slate-400'>
                                Nenhum usuário encontrado
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </SidebarField>

              <SidebarField icon={User} label='Solicitante'>
                {reporter ? (
                  <div className='flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2'>
                    <Avatar name={reporter.name} color={reporter.avatarColor} size='sm' />
                    <span className='truncate text-sm text-slate-700'>{reporter.name}</span>
                  </div>
                ) : (
                  <div className='devops-task-field__value text-slate-400'>Não informado</div>
                )}
              </SidebarField>

              <SidebarField icon={Tag} label='Etiquetas'>
                <div className='flex flex-wrap gap-2'>
                  {taskLabels.map((label) => (
                    <span
                      key={label!.id}
                      className='inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-1.5 py-1'
                    >
                      <LabelBadge name={label!.name} color={label!.color} />
                      {isEditing && (
                        <button
                          type='button'
                          onClick={() =>
                            updateDraftTask({
                              labelIds: draftTask.labelIds.filter((id) => id !== label!.id),
                            })
                          }
                          className='rounded p-0.5 text-slate-300 hover:bg-red-50 hover:text-red-500'
                          aria-label={`Remover ${label!.name}`}
                        >
                          <X size={12} />
                        </button>
                      )}
                    </span>
                  ))}
                  {taskLabels.length === 0 && !isEditing && (
                    <span className='text-xs text-slate-400'>Nenhuma etiqueta</span>
                  )}
                  {isEditing && (
                    <select
                      onChange={(event) => {
                        const value = event.target.value
                        if (value && !draftTask.labelIds.includes(value)) {
                          updateDraftTask({labelIds: [...draftTask.labelIds, value]})
                        }
                        event.target.value = ''
                      }}
                      className='w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm'
                      defaultValue=''
                      aria-label='Adicionar etiqueta'
                    >
                      <option value='' disabled>
                        Adicionar etiqueta...
                      </option>
                      {state.labels
                        .filter((item) => !draftTask.labelIds.includes(item.id))
                        .map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </select>
                  )}
                </div>
              </SidebarField>
            </section>

            <section className='devops-task-sidebar-section'>
              <h3 className='devops-task-sidebar-title'>
                <Clock3 size={15} />
                Histórico
              </h3>
              <div className='grid grid-cols-2 gap-3'>
                <SidebarField icon={Clock3} label='Criada'>
                  <div className='devops-task-field__value'>{formatDate(draftTask.createdAt)}</div>
                </SidebarField>
                <SidebarField icon={Clock3} label='Atualizada'>
                  <div className='devops-task-field__value'>{formatDate(draftTask.updatedAt)}</div>
                </SidebarField>
              </div>
            </section>
          </aside>
        </div>

        <footer className='devops-task-modal-footer'>
          <p className={saveError ? '!text-red-600' : ''}>
            {saveError ||
              (isEditing
                ? 'Revise os campos e salve quando terminar.'
                : 'Comentários e anexos continuam disponíveis durante a visualização.')}
          </p>
          <div className='devops-task-modal-footer__actions'>
            {isEditing ? (
              <>
                <button
                  type='button'
                  onClick={handleCancelEditing}
                  className='devops-secondary-button'
                  disabled={isSaving}
                >
                  <RotateCcw size={15} />
                  <span>Descartar</span>
                </button>
                <button
                  type='button'
                  onClick={handleSaveAll}
                  disabled={!isDirty || isSaving || !draftTask.title.trim()}
                  className='devops-primary-button disabled:cursor-not-allowed disabled:opacity-50'
                >
                  <Save size={16} />
                  <span>{isSaving ? 'Salvando...' : 'Salvar alterações'}</span>
                </button>
              </>
            ) : (
              <>
                <button
                  type='button'
                  onClick={handleRequestClose}
                  className='devops-secondary-button'
                >
                  Fechar
                </button>
                <button
                  type='button'
                  onClick={handleStartEditing}
                  className='devops-primary-button'
                >
                  <Edit3 size={16} />
                  Editar tarefa
                </button>
              </>
            )}
          </div>
        </footer>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title='Excluir tarefa'
        message='Tem certeza que deseja excluir esta tarefa? Esta ação não pode ser desfeita.'
        confirmLabel='Excluir'
        onConfirm={async () => {
          await apiDeleteTask(task.id)
          onClose()
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
      <ConfirmDialog
        isOpen={showDiscardConfirm}
        title='Descartar alterações'
        message='Existem alterações que ainda não foram salvas. Deseja fechar e descartá-las?'
        confirmLabel='Descartar'
        onConfirm={() => {
          setShowDiscardConfirm(false)
          onClose()
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </div>
  )
}

function SummaryCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType
  label: string
  value: string
}) {
  return (
    <div className='devops-task-summary-card'>
      <Icon size={17} />
      <span>
        <small>{label}</small>
        <strong>{value}</strong>
      </span>
    </div>
  )
}

function TaskTab({
  active,
  icon: Icon,
  label,
  count,
  onClick,
}: {
  active: boolean
  icon: React.ElementType
  label: string
  count: number
  onClick: () => void
}) {
  return (
    <button
      type='button'
      role='tab'
      aria-selected={active}
      onClick={onClick}
      className={`devops-task-tab${active ? ' is-active' : ''}`}
    >
      <Icon size={15} />
      {label}
      <span className='devops-task-tab__count'>{count}</span>
    </button>
  )
}

function ReadOnlySubtasks({
  subtasks,
  getUserById,
}: {
  subtasks: SubTask[]
  getUserById: (id: string) => {name: string; avatarColor: string} | undefined
}) {
  if (subtasks.length === 0) {
    return (
      <div className='devops-empty-state min-h-[150px]'>
        <div>
          <ListChecks size={24} className='mx-auto' />
          <p className='text-sm'>Nenhuma subtarefa cadastrada.</p>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-2'>
      {subtasks.map((subtask) => {
        const assignedUser = subtask.assigneeId ? getUserById(subtask.assigneeId) : undefined

        return (
          <div
            key={subtask.id}
            className='flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3'
          >
            <span
              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                subtask.completed
                  ? 'border-emerald-500 bg-emerald-500 text-white'
                  : 'border-slate-300'
              }`}
            >
              {subtask.completed && <CheckCircle2 size={12} />}
            </span>
            <span
              className={`min-w-0 flex-1 text-sm font-medium ${
                subtask.completed ? 'text-slate-400 line-through' : 'text-slate-700'
              }`}
            >
              {subtask.title}
            </span>
            {subtask.hours > 0 && (
              <span className='text-xs font-semibold text-slate-400'>{subtask.hours}h</span>
            )}
            {assignedUser && (
              <Avatar name={assignedUser.name} color={assignedUser.avatarColor} size='sm' />
            )}
          </div>
        )
      })}
    </div>
  )
}

function SidebarField({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ElementType
  label: string
  children: React.ReactNode
}) {
  return (
    <div className='devops-task-field'>
      <div className='devops-task-field__label'>
        <Icon size={13} />
        <span>{label}</span>
      </div>
      {children}
    </div>
  )
}
